/**
 * Positional part resolution for the mount path.
 *
 * Marker POSITIONS are a property of the compiled template, not of any clone.
 * The first clone of a template is walked once; what the walk found is compiled
 * into a cursor program plus one entry per part spec, and every later clone
 * replays that instead of searching for markers by name. The plan and the part
 * specs are both ordered, so a clone resolves its parts by POSITION: nothing is
 * looked up, and no map is allocated.
 *
 * Hydration does not come through here. It resolves markers against server DOM
 * — which is not a clone of anything and has no recorded shape to replay — with
 * its own walk and its own name-keyed maps, and binds through `buildDomParts`.
 */
import { endMarker, isAttributePartType, isElementPartType, runtimeError, } from './render-values.js';
import { createElementPart, createNodePart } from './render-dom-parts.js';
import { createAttributePart, stripStaticMarkerAttribute } from './render-dom-attribute-parts.js';
import { elidedNodeMarkers, siblingBoundedMarkers } from './render-part-elision.js';
import { getBinding } from './render-template.js';
import { isStaticBinding, isStaticSlotType } from './render-dom-static.js';
/** An element-part marker, carried as a valueless attribute NAME (G1). */
const ELEMENT_MARKER_NAME_RE = /^__h:\d+__$/;
/**
 * How one part spec — or one element-marker attribute awaiting removal —
 * resolves against a clone.
 *
 * `kind` says what the recording walk found: a comment, a marker inside an
 * attribute VALUE, or an element-part marker carried as an attribute NAME
 * (G1). `MARK_ABSENT` means the template carries no marker for that spec at
 * all, so an attribute or element part binds to nothing and commits nowhere —
 * exactly what a missing map entry used to mean. `slot` is where the cursor
 * program leaves the node, `name` is the comment's text or the attribute's
 * name, and `value` is the AUTHORED attribute text, which is what carries
 * composite segments and a single hole's prefix/suffix.
 */
export const MARK_ABSENT = 0;
export const MARK_COMMENT = 1;
export const MARK_ATTRIBUTE = 2;
export const MARK_ELEMENT = 3;
const ABSENT_ENTRY = { kind: MARK_ABSENT, slot: 0, name: '', value: '' };
/** The opening of every attribute-value and element-part marker. */
const MARKER_PREFIX = '__h:';
/**
 * A cursor program: one packed instruction per element, `op | (arg << 2)`.
 *
 * The instructions move a single cursor through a clone rather than walking
 * each marker's path from the fragment root, so markers sharing a descent pay
 * for it once. `DOWN` takes `firstChild` and then `arg` sibling hops, `SIB`
 * takes `arg` sibling hops, `UP` takes `arg` parent hops, and `EMIT` records
 * the cursor into slot `arg`. Every hop is a plain pointer read: no
 * `childNodes` collection is materialized and no map is allocated.
 */
const OP_DOWN = 0;
const OP_SIB = 1;
const OP_UP = 2;
const OP_EMIT = 3;
/**
 * One plan per compiled `<template>` element, keyed by that element.
 *
 * Keying on the parsed template rather than on the `Template` record is what
 * makes the cache safe across documents (#1244): `ensureDomTemplate` throws its
 * memo away and reparses when the owning document changes, and the plan
 * describing the discarded parse becomes unreachable with it. The entry can
 * therefore never outlive — or be replayed against a clone of — a different
 * parse than the one it was recorded from, and nothing here retains a DOM node
 * the runtime was not already retaining.
 */
const clonePlans = new WeakMap();
function commentKeyOf(node) {
    const comment = node;
    return String(comment.data ?? comment.nodeValue ?? '').trim();
}
/**
 * Emits the cursor moves that take the program from `from` to `to`.
 *
 * Where both paths still have a level at the first divergence, the walk stops
 * one level short of it and steps SIDEWAYS: marks are claimed in document
 * order, so the target is always a later sibling, and one sibling hop replaces
 * a climb to the parent plus a descent by index.
 */
function emitMoves(code, from, to) {
    let common = 0;
    while (common < from.length && common < to.length && from[common] === to[common])
        common += 1;
    const across = common < from.length && common < to.length ? 1 : 0;
    if (from.length - common - across > 0) {
        code.push(OP_UP | ((from.length - common - across) << 2));
    }
    if (across)
        code.push(OP_SIB | (((to[common] ?? 0) - (from[common] ?? 0)) << 2));
    for (let depth = common + across; depth < to.length; depth += 1) {
        code.push(OP_DOWN | ((to[depth] ?? 0) << 2));
    }
}
/** Moves the program's cursor onto `path` and gives that node a slot. */
function claimSlot(rec, path) {
    emitMoves(rec.code, rec.cursor, path);
    rec.cursor = path.slice();
    const slot = rec.slots;
    rec.slots = slot + 1;
    rec.code.push(OP_EMIT | (slot << 2));
    return slot;
}
/**
 * The element's attribute names, as a snapshot, WITHOUT materializing an `Attr`
 * node for any of them.
 *
 * `element.attributes` is a live `NamedNodeMap`, and reading an entry out of it
 * forces the engine to build a real `Attr` node — a DOM node that then lives as
 * long as the element does and is counted by every DOM-node census. This walk
 * runs on the first clone of every template, and the clone of a shell that
 * mounts once is never discarded, so those nodes stay for the life of the
 * document: 17 of them on the react-comparison benchmark's own markup, against
 * React's zero, which was 17 of its 70-node retained-node gap.
 * `getAttributeNames()` hands back plain strings and `getAttribute()` reads the
 * same storage without materializing anything.
 *
 * Snapshot semantics are load-bearing, exactly as `Array.from` made them: a
 * caller that removes an attribute while iterating would skip entries in a live
 * map. Exported because the hydration walk needs the same guarantee.
 */
export function attributeNamesOf(element) {
    const names = element.getAttributeNames?.();
    if (names)
        return names;
    // A DOM without `getAttributeNames` has no `Attr` node to materialize either,
    // so the fallback costs nothing it is trying to avoid.
    return Array.from(element.attributes ?? [], (attribute) => attribute.name);
}
/**
 * Walks a cloned fragment once, recording every marker it meets and emitting
 * the program that will find them again in any later clone.
 *
 * `path` is one array reused across the whole walk (pushed on descent, popped
 * on return). An element with markers claims ONE slot however many it carries,
 * so a `class` and a `data-*` binding on the same tag cost one descent between
 * them. The walk reads and records only — the clone is not mutated, so an
 * abandoned plan leaves it exactly as it found it.
 */
function recordMarks(node, path, rec) {
    const nodeType = node.nodeType;
    if (nodeType === 8) {
        rec.marks.push({
            kind: MARK_COMMENT,
            slot: claimSlot(rec, path),
            name: commentKeyOf(node),
            value: '',
        });
    }
    else if (nodeType === 1) {
        let slot = -1;
        const element = node;
        for (const name of attributeNamesOf(element)) {
            const value = String(element.getAttribute(name) || '');
            const isElementMarker = ELEMENT_MARKER_NAME_RE.test(name);
            if (!isElementMarker && !value.includes(MARKER_PREFIX))
                continue;
            if (slot < 0)
                slot = claimSlot(rec, path);
            rec.marks.push({
                kind: isElementMarker ? MARK_ELEMENT : MARK_ATTRIBUTE,
                slot,
                name,
                value: isElementMarker ? '' : value,
            });
        }
    }
    const children = node.childNodes;
    const childCount = children?.length ?? 0;
    for (let index = 0; index < childCount; index += 1) {
        const child = children?.[index];
        if (!child)
            continue;
        path.push(index);
        recordMarks(child, path, rec);
        path.pop();
    }
}
/**
 * The LAST mark carrying `marker`, or null.
 *
 * Last, not first, because the name-keyed maps this replaces wrote each key as
 * the walk met it, so a duplicate key resolved to the last node carrying it.
 * An attribute is matched on its authored VALUE because one composite
 * (`attr="a ${x} b ${y}"`) carries several markers; markers are delimited on
 * both sides, so `__h:1__` is never a substring of `__h:11__`.
 */
function findMark(marks, kind, marker) {
    let found = null;
    for (const mark of marks) {
        if (mark.kind !== kind)
            continue;
        const hit = kind === MARK_ATTRIBUTE ? mark.value.includes(marker) : mark.name === marker;
        if (hit)
            found = mark;
    }
    return found;
}
/** Pairs each part spec with the mark it resolved to, in spec order. */
function compilePlan(specs, rec) {
    const entries = [];
    for (const spec of specs) {
        let kind = MARK_COMMENT;
        if (isElementPartType(spec.type))
            kind = MARK_ELEMENT;
        else if (isAttributePartType(spec.type))
            kind = MARK_ATTRIBUTE;
        entries.push(findMark(rec.marks, kind, spec.marker) ?? ABSENT_ENTRY);
    }
    const specCount = entries.length;
    for (const mark of rec.marks) {
        if (mark.kind === MARK_ELEMENT && !entries.includes(mark))
            entries.push(mark);
    }
    return { program: rec.code, specCount, entries };
}
/** Runs a cursor program, filling `slots`; false the instant a hop runs out of tree. */
function runProgram(root, program, slots) {
    let node = root;
    for (let index = 0; index < program.length; index += 1) {
        const code = program[index] ?? 0;
        const op = code & 3;
        const arg = code >> 2;
        if (op === OP_EMIT) {
            slots.push(node);
            continue;
        }
        if (op === OP_UP) {
            for (let hop = 0; hop < arg && node !== null; hop += 1)
                node = node.parentNode;
        }
        else {
            if (op === OP_DOWN && node !== null)
                node = node.firstChild;
            for (let hop = 0; hop < arg && node !== null; hop += 1)
                node = node.nextSibling;
        }
        if (node === null)
            return false;
    }
    return true;
}
/**
 * Checks every slot the program filled against what the recording said sits
 * there — node type, comment text, and the attribute's authored value.
 *
 * A single disagreement abandons the whole replay. That check is what makes
 * the plan safe rather than merely fast: a clone whose shape does not match the
 * recording falls back to a fresh walk instead of binding live parts to the
 * wrong nodes. It runs to completion before anything is built, so no DOM is
 * touched and no part exists until the entire plan has resolved.
 */
function planResolves(plan, slots) {
    for (const entry of plan.entries) {
        if (entry.kind === MARK_ABSENT)
            continue;
        const node = slots[entry.slot];
        if (!node)
            return false;
        if (entry.kind === MARK_COMMENT) {
            if (node.nodeType !== 8 || commentKeyOf(node) !== entry.name)
                return false;
            continue;
        }
        // An element-part marker is a valueless attribute, so its recorded value is
        // the empty string and this same comparison proves it is still present.
        if (node.nodeType !== 1)
            return false;
        if (node.getAttribute(entry.name) !== entry.value)
            return false;
    }
    return true;
}
/**
 * Builds one clone's parts — and its static slots — from a resolved plan.
 *
 * Null means the plan did not resolve against this clone — a hop ran out of
 * tree, or a slot disagreed with the recording — and the caller walks afresh.
 * Nothing has run by then.
 */
function bindPlan(template, fragment, plan, documentObj, domAdapter, bindings) {
    const slots = [];
    if (!runProgram(fragment, plan.program, slots))
        return null;
    if (!planResolves(plan, slots))
        return null;
    const elided = elidedNodeMarkers(template);
    const siblingBounded = siblingBoundedMarkers(template);
    const specs = template.partSpecs;
    const parts = [];
    const staticIndices = [];
    const staticRefs = [];
    for (let index = 0; index < plan.specCount; index += 1) {
        const spec = specs[index];
        const entry = plan.entries[index];
        if (!spec || !entry)
            return null;
        const node = entry.kind === MARK_ABSENT ? null : (slots[entry.slot] ?? null);
        if (isElementPartType(spec.type)) {
            parts.push(createElementPart(spec, node ?? undefined));
            continue;
        }
        // A static binding of a slot-eligible type commits once on the install
        // pass and costs the clone NO part object: the slot keeps only the DOM
        // reference a later promotion (#678) would need, and the commit state is
        // re-derived from the shared plan entry when — and if — it is ever
        // re-read (#1398).
        if (entry.kind !== MARK_ABSENT && isStaticSlotType(spec)) {
            const binding = getBinding(bindings, spec);
            if (isStaticBinding(spec, binding)) {
                const ref = staticSlotRef(spec, entry, node, elided, siblingBounded);
                if (ref) {
                    staticIndices.push(index);
                    staticRefs.push(ref);
                    continue;
                }
                // No usable DOM reference fell out of the marker: bind a live part
                // below, whose own missing-element handling keeps it inert.
            }
        }
        if (isAttributePartType(spec.type)) {
            parts.push(createAttributePart(spec, node ?? undefined, entry.name, entry.value));
            continue;
        }
        const anchor = node;
        const parent = anchor?.parentNode;
        if (!anchor || !parent) {
            throw runtimeError('RT401', `Missing comment marker "${spec.marker}" for node part ${spec.index} — the template's static HTML may be malformed.`);
        }
        // A marker that was its parent element's only content needs no boundaries
        // of its own (#1354): the element's are already exactly that. The clone
        // sheds the marker and the part takes the element as its range instead, so
        // the row carries two fewer nodes per hole and creates no end comment.
        if (elided.has(spec.marker) && !siblingBounded.has(spec.marker)) {
            parent.removeChild(anchor);
            parts.push(createNodePart(spec, undefined, undefined, parent, documentObj, domAdapter));
            continue;
        }
        // A marker bounded by static siblings (#1390) sheds its marker too: the
        // neighbours the string promised must exist in this clone, or the string
        // and the tree disagree — malformed static HTML — and binding a range the
        // server's bytes cannot describe would diverge from the string renderer.
        // A bound may be an element (tags touching the marker) or the
        // whitespace-only text node beside it; either is a stable node nothing
        // else ever removes.
        if (siblingBounded.has(spec.marker)) {
            const before = anchor.previousSibling;
            const after = anchor.nextSibling;
            if (before === null ||
                after === null ||
                !isBindableBound(before) ||
                !isBindableBound(after) ||
                before.parentNode !== parent ||
                after.parentNode !== parent) {
                throw runtimeError('RT401', `Marker "${spec.marker}" promises sibling bounds the clone does not have — the template's static HTML may be malformed.`);
            }
            parent.removeChild(anchor);
            parts.push(createNodePart(spec, before, after, undefined, documentObj, domAdapter));
            continue;
        }
        const end = documentObj.createComment(endMarker(spec.index));
        parent.insertBefore(end, anchor.nextSibling);
        parts.push(createNodePart(spec, anchor, end, undefined, documentObj, domAdapter));
    }
    // The walk leaves element-part marker attributes in place so the plan can
    // verify them; the bind owes the clone their removal, so they never reach
    // the page.
    for (const entry of plan.entries) {
        if (entry.kind !== MARK_ELEMENT)
            continue;
        const element = slots[entry.slot];
        if (element)
            element.removeAttribute(entry.name);
    }
    partitionStaticSlots(specs, staticIndices, staticRefs);
    return { parts, staticIndices, staticRefs, plan };
}
/**
 * Whether a clone or live node can serve as a sibling-bounded part's range
 * bound (#1390): an element whose tags touched the marker, or the
 * whitespace-only text node that sat beside it. Both are static template
 * nodes nothing in the framework ever removes, which is the whole contract
 * the range walk relies on. Anything else — a text node with content, another
 * comment — means the tree disagrees with what the predicate read out of the
 * string. Shared with the hydration walker, which validates the same
 * contract against the live tree (#1441).
 */
export function isBindableBound(node) {
    if (!node)
        return false;
    if (node.nodeType === 1)
        return true;
    return node.nodeType === 3 && /^[\t\n\f\r ]*$/.test(node.nodeValue ?? '');
}
/**
 * The DOM reference a static slot retains, or null when its marker resolved
 * to nothing usable and the caller should bind a live part instead.
 *
 * An attribute-family slot keeps its element (stripping the authored marker
 * attribute exactly as `createAttributePart` would). A node-family slot keeps
 * its range parent ONLY when the marker was elided as sole content (#1354) —
 * a sibling-bounded hole (#1390) shares its parent with static nodes the slot
 * commit must not disturb, and a non-elided marker means the hole keeps a
 * live part and its `<!--/h:N-->` end comment, because the string renderer
 * emits both boundaries and the DOM must agree byte for byte. Elision is
 * exactly where an earlier attempt diverged (#1354), so the slot path refuses
 * every shape it cannot commit into blindly rather than half of it.
 */
function staticSlotRef(spec, entry, node, elided, siblingBounded) {
    if (isAttributePartType(spec.type)) {
        const element = node;
        if (!element)
            return null;
        stripStaticMarkerAttribute(spec, element, entry.name);
        return element;
    }
    if (!elided.has(spec.marker) || siblingBounded.has(spec.marker))
        return null;
    const anchor = node;
    const parent = anchor?.parentNode;
    if (!anchor || !parent)
        return null;
    parent.removeChild(anchor);
    return parent;
}
/**
 * Stable-partitions the slots the way `indexParts` partitions parts: node
 * family first, then attribute family, each preserving spec order. The
 * install and update passes then walk one cursor over each family and every
 * commit — slot or part — lands in exactly its G10 position.
 */
function partitionStaticSlots(specs, staticIndices, staticRefs) {
    const deferredIndices = [];
    const deferredRefs = [];
    let write = 0;
    for (let read = 0; read < staticIndices.length; read += 1) {
        const index = staticIndices[read];
        const ref = staticRefs[read];
        const spec = index === undefined ? undefined : specs[index];
        if (index === undefined || ref === undefined || !spec)
            continue;
        if (isAttributePartType(spec.type)) {
            deferredIndices.push(index);
            deferredRefs.push(ref);
            continue;
        }
        staticIndices[write] = index;
        staticRefs[write] = ref;
        write += 1;
    }
    for (let read = 0; read < deferredIndices.length; read += 1) {
        const index = deferredIndices[read];
        const ref = deferredRefs[read];
        if (index === undefined || ref === undefined)
            continue;
        staticIndices[write] = index;
        staticRefs[write] = ref;
        write += 1;
    }
    staticIndices.length = write;
    staticRefs.length = write;
}
/**
 * Mount-path entry point: bind one freshly cloned fragment.
 *
 * Takes the whole `Template` rather than just its part specs because the
 * compiled `<template>` on it is what keys the plan, and the instance's
 * bindings because a binding that is static at mount demotes the slot from a
 * part object to a static slot (#1398) — a decision only the clone itself can
 * make.
 *
 * The first clone of a template is walked, and what the walk found is compiled
 * into the plan every later clone replays. A replay that disagrees with the
 * recording throws its plan away and walks again — so a clone the plan does not
 * describe costs one extra walk, never a part bound to the wrong node.
 */
export function prepareDomParts(template, fragment, documentObj, domAdapter, bindings) {
    const source = template.domTemplate;
    const cached = source ? clonePlans.get(source) : undefined;
    if (cached) {
        const prepared = bindPlan(template, fragment, cached, documentObj, domAdapter, bindings);
        if (prepared)
            return prepared;
        if (source)
            clonePlans.delete(source);
    }
    const recorder = { code: [], marks: [], cursor: [], slots: 0 };
    recordMarks(fragment, [], recorder);
    const plan = compilePlan(template.partSpecs, recorder);
    if (source)
        clonePlans.set(source, plan);
    const prepared = bindPlan(template, fragment, plan, documentObj, domAdapter, bindings);
    if (prepared)
        return prepared;
    throw runtimeError('RT401', "This template's markers could not be resolved against its own clone.");
}
//# sourceMappingURL=render-dom-plan.js.map